import { useContext, useEffect, useMemo, useState } from 'react';
import {
  COMPLETE,
  PENDING
} from 'app/common/hooks/useRestApi/RequestStatus.js';
import { get, isEmpty } from 'lodash';
import ContentModelContext, {
  generateContentKey,
  addContentModel,
  getContentModel,
  getModelKeyGen,
  getFallbackKeys,
  useGetContentModelKeyGens
} from 'app/content/contexts/ContentModelContext.js';
import useFetchContent from '../useFetchContent';

/**
 * Generic hook to manage content retrieval and caching.
 * Resolves appropriate content from Context or fetches it from the API if missing.
 *
 * @param {string} modelName - The name of the content model to fetch.
 * @param {Object} options - { useHierarchy: boolean, fallbackValues: Object, uri: string }
 * @returns {{content: Object, loading: boolean, error: Object}}
 */
const useContentModel = (
  modelName,
  { uri = undefined, useHierarchy = true, fallbackValues = {} } = {}
) => {
  const contentModelKeyGens = useGetContentModelKeyGens();
  const contentModelContext = useContext(ContentModelContext);
  const [done, setDone] = useState(false);

  const identifier = modelName || uri;

  // 1. Initialize fetch state but don't fire yet
  const { requestStatus, sendCallback, response, loading, error } =
    useFetchContent({
      modelName,
      uri,
      sendImmediately: false
    });

  // 2. Resolve the best available key (Store > Dealer > Tenant)
  const primaryKeyGen = useMemo(() => {
    return getModelKeyGen(contentModelKeyGens);
  }, [contentModelKeyGens]);

  const fallbackKeys = useMemo(
    () =>
      useHierarchy
        ? getFallbackKeys(contentModelKeyGens).map(keyGen =>
            generateContentKey(identifier, keyGen)
          )
        : [],
    [contentModelKeyGens, useHierarchy, identifier]
  );

  // 3. Effect to manage the fetch lifecycle
  useEffect(() => {
    if (!identifier || !primaryKeyGen || done) {
      return;
    }

    const existing = getContentModel({
      name: identifier,
      keyGen: primaryKeyGen,
      contentModelContext,
      fallbackKeys
    });

    if (existing) {
      setDone(true);
      return;
    }

    if (requestStatus === PENDING) {
      sendCallback(); // Auto-fetch if cache miss
    }
  }, [
    identifier,
    primaryKeyGen,
    contentModelContext,
    fallbackKeys,
    requestStatus,
    sendCallback,
    done
  ]);

  // 4. Effect to populate context when fetch completes
  useEffect(() => {
    if (done || requestStatus !== COMPLETE) {
      return;
    }

    const results = response?.results;
    const contentToCache = isEmpty(results) ? { model: null } : results[0];

    addContentModel({
      name: identifier,
      content: contentToCache,
      keyGen: primaryKeyGen,
      contentModelContext
    });

    setDone(true);
  }, [
    done,
    requestStatus,
    response,
    identifier,
    primaryKeyGen,
    contentModelContext
  ]);

  // 5. Return a unified object
  let content = fallbackValues;
  if (isPreview(primaryKeyGen)) {
    // Since the cache is not used in preview mode, we need to get the results directly from the response.
    if (requestStatus === COMPLETE) {
      const results = response?.results;
      content = isEmpty(results) ? { model: null } : results[0];
    }
  } else {
    content = getContentModel({
      name: identifier,
      keyGen: primaryKeyGen,
      contentModelContext,
      fallbackKeys,
      fallbackValue: fallbackValues
    });
  }

  return {
    content,
    loading: !done && (loading || (requestStatus === PENDING && !content)),
    error
  };
};

function isPreview(keyGen) {
  return get(keyGen, 'isPreview', false);
}

export default useContentModel;
